// externals
import { createSelector } from 'reselect';

// types
import type {
    PurchaseHistoryCard,
    PurchaseHistoryRate,
    PurchaseHistoryRecordServiceData,
    PurchaseHistorySectionState,
    PurchaseHistoryState,
    StateTree
} from '@makemydeal/dr-dash-types';
import { PURCHASE_HISTORY_MAX_RECORDS, PURCHASE_HISTORY_PLACEHOLDER } from '@makemydeal/dr-dash-types';

import { initialState } from '../reducers/purchaseHistoryReducer';

/** "Not on file" becomes a visible dash rather than an empty cell, a bare '0', or 'undefined'. */
const withPlaceholder = (value?: string | null): string => {
    if (value === null || value === undefined) {
        return PURCHASE_HISTORY_PLACEHOLDER;
    }
    return value.trim() === '' ? PURCHASE_HISTORY_PLACEHOLDER : value;
};

const hasText = (value: string | null): boolean => typeof value === 'string' && value.trim() !== '';

/**
 * Whether a record is worth putting on a card at all. These two are DROPPED rather than
 * placeholder-filled — the em-dash exceptions:
 *
 *  - **Vehicle** (make AND model; a bare year identifies nothing). The vehicle is what identifies a
 *    purchase; without it the figures describe something the manager cannot place.
 *  - **Deal type.** How the customer paid is the frame for every money figure on the card. Without
 *    it a payment and a gross cannot be read as finance, lease or cash terms, and the rate row is
 *    suppressed too — so the card is a row of numbers with nothing to interpret them by.
 *
 * Everything else missing is a dash on an otherwise readable card.
 */
const isRenderable = (record: PurchaseHistoryRecordServiceData): boolean =>
    hasText(record.make) && hasText(record.model) && hasText(record.dealType);

/** Composes 'YYYY Make Model Trim' from whichever parts are present. */
const composeVehicle = (record: PurchaseHistoryRecordServiceData): string => {
    const parts = [record.year === null ? null : String(record.year), record.make, record.model, record.trim];
    return parts
        .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
        .map((part) => part.trim())
        .join(' ');
};

/**
 * ISO `YYYY-MM-DD` to `MM/DD/YYYY`, read from the string rather than via `Date`:
 * `new Date('2022-03-28')` is UTC midnight, which renders a day earlier in every US timezone.
 */
const formatSoldDate = (soldDate: string | null): string => {
    if (!soldDate) {
        return PURCHASE_HISTORY_PLACEHOLDER;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(soldDate);
    if (!match) {
        return PURCHASE_HISTORY_PLACEHOLDER;
    }
    const [, year, month, day] = match;
    return `${month}/${day}/${year}`;
};

/** Currency with a symbol and thousands separators. `decimals` distinguishes a payment from a gross. */
const formatCurrency = (value: number | null, decimals: number): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return PURCHASE_HISTORY_PLACEHOLDER;
    }
    const formatted = Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
    return value < 0 ? `-$${formatted}` : `$${formatted}`;
};

/** A rate as a percentage, with up to two decimals and no trailing zeros. */
const formatRate = (value: number | null): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return PURCHASE_HISTORY_PLACEHOLDER;
    }
    return `${Number(value.toFixed(2))}%`;
};

/**
 * A money factor is NOT a percentage: `0.00125` through `formatRate` would render as `0%`, because
 * `toFixed(2)` swallows it. So no `%`, and five decimals — the convention money factors are quoted
 * to, and the reason the width is FIXED rather than merely capped.
 *
 * Padding is the only option: the source sends a number, so `0.01000000` has already become `0.01`
 * by the time it reaches here — `JSON.parse` discards trailing zeros and they cannot be recovered.
 * Without a minimum, `0.01` renders three digits narrower than `0.00125` and reads as a different
 * kind of figure in the same column.
 */
const formatMoneyFactor = (value: number | null): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return PURCHASE_HISTORY_PLACEHOLDER;
    }
    return value.toLocaleString('en-US', { minimumFractionDigits: 5, maximumFractionDigits: 5 });
};

/**
 * The rate row, or `null` when the deal type has no rate to show.
 *
 * The buy side is always the DMS buy rate. Only the SELL side varies:
 *   Finance | the APR, a percentage
 *   Lease   | the money factor, which is not a percentage
 *   Cash    | no row — no rate applies
 *   unknown | no row — no deal type, so no basis for choosing a sell side
 */
const composeRate = (record: PurchaseHistoryRecordServiceData): PurchaseHistoryRate | null => {
    if (record.dealType === 'Finance') {
        return { buyRate: formatRate(record.buyRate), sellRate: formatRate(record.sellRate) };
    }

    if (record.dealType === 'Lease') {
        // `sellRate` is deliberately ignored: on a lease the sell side IS the money factor, so a
        // populated APR must not leak into a row the manager reads as lease terms.
        return { buyRate: formatRate(record.buyRate), sellRate: formatMoneyFactor(record.moneyFactor) };
    }

    return null;
};

/** Product names only; the prices are not shown. Null or zero price means no chip. */
const composeVppLabels = (record: PurchaseHistoryRecordServiceData): string[] => {
    const labels: string[] = [];
    const present = (price: number | null | undefined) => typeof price === 'number' && price !== 0;

    if (present(record.vpp?.gap)) {
        labels.push('GAP');
    }
    if (present(record.vpp?.warranty)) {
        labels.push('Warranty');
    }
    return labels;
};

/** Converts one source record into the display form the cards render verbatim. */
export const toDisplayRecord = (record: PurchaseHistoryRecordServiceData): PurchaseHistoryCard => {
    return {
        soldDate: formatSoldDate(record.soldDate),
        vehicle: withPlaceholder(composeVehicle(record)),
        dealType: withPlaceholder(record.dealType),
        paymentAmount:
            record.monthlyPayment === null ? PURCHASE_HISTORY_PLACEHOLDER : `${formatCurrency(record.monthlyPayment, 2)}/mo`,
        termMonths: record.termMonths === null ? PURCHASE_HISTORY_PLACEHOLDER : String(record.termMonths),
        // A count, not display text: the em-dash guard would collapse 'paid off' (0) into 'not known'.
        remainingPayments: record.remainingPayments ?? null,
        frontGross: formatCurrency(record.frontGross, 0),
        backGross: formatCurrency(record.backGross, 0),
        rate: composeRate(record),
        vpps: composeVppLabels(record)
    };
};

/** Sort key only, never displayed. `undefined` for the placeholder, which sorts last. */
const toSortKey = (soldDate: string): number | undefined => {
    if (soldDate === PURCHASE_HISTORY_PLACEHOLDER) {
        return undefined;
    }
    const parsed = Date.parse(soldDate);
    // Defensive and excluded from coverage: `formatSoldDate` only emits the placeholder or a valid
    // date, so this cannot fail today — but a format change would silently corrupt the ordering.
    /* istanbul ignore next */
    return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Most-recent first, undated last. Equal keys retain supplied order (sort is stable since ES2019).
 */
const byMostRecentSoldDate = (left: PurchaseHistoryCard, right: PurchaseHistoryCard): number => {
    const leftKey = toSortKey(left.soldDate);
    const rightKey = toSortKey(right.soldDate);

    if (leftKey === undefined && rightKey === undefined) return 0;
    if (leftKey === undefined) return 1;
    if (rightKey === undefined) return -1;

    return rightKey - leftKey;
};

export const getPurchaseHistory = (state: StateTree): PurchaseHistoryState => state?.purchaseHistory ?? initialState;

export const getIsPurchaseHistoryRequestOutstanding = (state: StateTree): boolean => getPurchaseHistory(state).isRequestOutstanding;

export const getPurchaseHistoryRequestedAtMs = (state: StateTree): number | null => getPurchaseHistory(state).requestedAtMs;

export const getIsPurchaseHistoryNotificationRaised = (state: StateTree): boolean =>
    getPurchaseHistory(state).notification.isRaised;

export const getPurchaseHistoryScrollRequestId = (state: StateTree): string | boolean =>
    getPurchaseHistory(state).notification.uniqueScrollRequestId;

/**
 * Memoised: the result is a freshly-built object, and `useSelector` compares by reference — without
 * this the section re-renders on every action dispatched anywhere in Manager View.
 */
export const getPurchaseHistorySectionState = createSelector([getPurchaseHistory], (slice): PurchaseHistorySectionState => {
    if (slice.status === 'loading') {
        return { status: 'loading', isTakingLonger: slice.isTakingLonger };
    }

    if (slice.status === 'unavailable') {
        return { status: 'unavailable' };
    }

    if (slice.status === 'empty') {
        return { status: 'empty' };
    }

    // Dropped BEFORE map and sort, so it costs no conversion and cannot influence ordering.
    const renderableRecords = slice.records.filter(isRenderable);

    if (renderableRecords.length === 0) {
        // Nothing renderable — to the manager, the same as having none.
        return { status: 'empty' };
    }

    return {
        status: 'ready',
        // Sliced AFTER the drop and the sort, so these are the latest N *renderable* records. Slicing
        // earlier could show fewer than N whenever an unidentifiable record fell inside the window.
        records: renderableRecords.map(toDisplayRecord).sort(byMostRecentSoldDate).slice(0, PURCHASE_HISTORY_MAX_RECORDS)
    };
});
